// CHOOSING A CLIENT BY TYPING. Every form that asks "which client" — a meeting, a task, a
// job, an engagement, linking an outside person's meetings — used a plain <select>, and with
// every client and lead in it that is a long list to scroll. This is one box: type part of
// a name (or a contact's) and the list narrows; arrows and Enter pick, Escape gives up.
//
// It holds no value of its own. `value` is the chosen id ('' for none) and `onChange`
// receives the new id as a string — what a <select>'s e.target.value carried — so a form
// keeps its state exactly as before. While the box is not being typed in it shows the
// chosen client's name; leaving it mid-search puts that name back, so a half-typed word is
// never mistaken for a choice.
//
// `emptyLabel` adds a first option meaning "no client" (the task form's "פנימי"); without it
// the form must choose one.
import { useId, useRef, useState } from 'react';
import he from '../i18n/he';
import { Field } from './Form';

// Compared ignoring case, spacing, direction marks and the quote marks Hebrew abbreviations
// carry (בע״מ), so "בעמ" finds it.
export const normalize = (text) => String(text || '')
  .replace(/[‎‏‪-‮"'״׳]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

// The clients a typed text matches: by name, or by any of their contacts (contacts_text,
// which every client list carries — the clients and leads searches match the same).
export const matchClients = (clients, query) => {
  const q = normalize(query);
  if (!q) return clients;
  return clients.filter((c) => normalize(c.name).includes(q) || normalize(c.contacts_text).includes(q));
};

export default function ClientPicker({ label, value, onChange, clients, emptyLabel, disabled }) {
  const listId = useId();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null); // null: not typing — the box shows the choice
  const [active, setActive] = useState(0);

  const chosen = clients.find((c) => String(c.id) === String(value));
  const options = [
    ...(emptyLabel ? [{ id: '', name: emptyLabel }] : []),
    ...matchClients(clients, query || ''),
  ];
  // The "no client" option stays only while nothing is typed: typing is looking for one.
  const shown = query ? options.filter((o) => o.id !== '') : options;

  const close = () => { setOpen(false); setQuery(null); };
  const pick = (option) => {
    onChange(String(option.id));
    close();
  };
  const openList = () => { setOpen(true); setActive(0); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openList(); else setActive((i) => Math.min(i + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // Enter inside the box picks; it never submits the dialog around it.
      if (open && shown[active]) { e.preventDefault(); pick(shown[active]); }
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    }
  };

  return (
    <Field label={label}>
      <div className={`client-picker${open ? ' open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={he.common.typeToSearch}
          value={query ?? (chosen ? chosen.name : (value === '' && emptyLabel && !open ? emptyLabel : ''))}
          onFocus={(e) => { openList(); e.target.select(); }}
          onClick={() => { if (!open) openList(); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onBlur={close}
          onKeyDown={onKeyDown}
        />
        {open && (
          <ul className="client-picker-list" id={listId} role="listbox">
            {shown.length === 0 && <li className="client-picker-empty">{he.common.noMatches}</li>}
            {shown.map((o, i) => (
              <li
                key={o.id === '' ? 'none' : o.id}
                role="option"
                aria-selected={String(o.id) === String(value)}
                className={[
                  i === active ? 'active' : '',
                  String(o.id) === String(value) ? 'chosen' : '',
                ].filter(Boolean).join(' ') || undefined}
                // mousedown, not click: a click lands after the input's blur has closed the list.
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                onMouseEnter={() => setActive(i)}
              >
                {o.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}
